import { createContext, useContext, useEffect, useRef, useState } from "react";
import { trackPlay } from "@/lib/tracking";
import { api } from "@/lib/api";

const PlayerCtx = createContext(null);
const HEARTBEAT_MS = 10000;

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  if (audioRef.current == null) audioRef.current = new Audio();
  const [current, setCurrent] = useState(null); // {number, title, audio_url, recap_note, next_part}
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pendingNext, setPendingNext] = useState(null); // {number, title, part, countdown}
  const pendingTimerRef = useRef(null);
  const cancelAutoPlay = () => {
    if (pendingTimerRef.current) {
      clearInterval(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    setPendingNext(null);
  };

  useEffect(() => {
    const a = audioRef.current;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      // Auto-play next part: if current has a next_part, queue a 3-second countdown
      setCurrent((cur) => {
        if (cur?.next_part) {
          setPendingNext({ ...cur.next_part, countdown: 3 });
        }
        return cur;
      });
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  // Run the countdown for pendingNext and fire autoplay at 0
  useEffect(() => {
    if (!pendingNext) return;
    if (pendingTimerRef.current) clearInterval(pendingTimerRef.current);
    pendingTimerRef.current = setInterval(() => {
      setPendingNext((p) => {
        if (!p) return p;
        if (p.countdown <= 1) {
          // Fire autoplay (fetch full ep and play)
          clearInterval(pendingTimerRef.current);
          pendingTimerRef.current = null;
          api.get(`/episodes/${p.number}`).then(({ data }) => {
            play(data);
          }).catch(() => {});
          return null;
        }
        return { ...p, countdown: p.countdown - 1 };
      });
    }, 1000);
    return () => {
      if (pendingTimerRef.current) clearInterval(pendingTimerRef.current);
      pendingTimerRef.current = null;
    };
  }, [pendingNext?.number]);

  // Periodically save playback position so we can resume later
  useEffect(() => {
    if (!playing || !current?.number) return;
    const a = audioRef.current;
    const beat = setInterval(() => {
      const pos = a.currentTime || 0;
      if (pos < 2) return; // ignore the very first moments
      api.post("/plays/position", { episode_number: current.number, position: pos }).catch(() => {});
    }, HEARTBEAT_MS);
    return () => clearInterval(beat);
  }, [playing, current?.number]);

  const play = (ep, opts = {}) => {
    // If something is queued from a previous ep ending, kill it (the user manually picked a new one)
    if (pendingTimerRef.current) {
      clearInterval(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    setPendingNext(null);
    const a = audioRef.current;
    const isNew = !current || current.number !== ep.number || current.audio_url !== ep.audio_url;
    if (isNew) {
      a.src = ep.audio_url;
      setCurrent(ep);
      trackPlay(ep.number, ep.voice || null);
      const startAt = Number.isFinite(opts.startAt) ? opts.startAt : 0;
      if (startAt > 1) {
        const seekWhenReady = () => {
          try { a.currentTime = startAt; setProgress(startAt); } catch { /* ignored */ }
          a.removeEventListener("loadedmetadata", seekWhenReady);
        };
        a.addEventListener("loadedmetadata", seekWhenReady);
      }
    }
    a.play().catch(() => {});
    setPlaying(true);
  };

  const pause = () => { audioRef.current.pause(); setPlaying(false); };
  const toggle = () => { if (playing) pause(); else if (current) { audioRef.current.play().catch(() => {}); setPlaying(true); } };
  const seek = (t) => { audioRef.current.currentTime = t; setProgress(t); };

  return (
    <PlayerCtx.Provider value={{ current, playing, progress, duration, play, pause, toggle, seek, pendingNext, cancelAutoPlay }}>
      {children}
    </PlayerCtx.Provider>
  );
};

export const usePlayer = () => useContext(PlayerCtx);
